import { Label } from "@modules/common/components/ui"
import { Eye, EyeSlash } from "@medusajs/icons"
import {
  localizeFrontendFieldLabel,
  useFrontendPreferences,
} from "@lib/frontend-preferences"
import React, { useEffect, useImperativeHandle, useState } from "react"


type InputProps = Omit<
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
  "placeholder"
> & {
  label: string
  errors?: Record<string, unknown>
  touched?: Record<string, unknown>
  name: string
  topLabel?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ type, name, label, touched: _touched, required, topLabel, ...props }, ref) => {
    const { locale } = useFrontendPreferences()
    const inputRef = React.useRef<HTMLInputElement>(null)
    const [showPassword, setShowPassword] = useState(false)
    const [inputType, setInputType] = useState(type)

    useEffect(() => {
      if (type === "password" && showPassword) {
        setInputType("text")
      }

      if (type === "password" && !showPassword) {
        setInputType("password")
      }
    }, [type, showPassword])

    useImperativeHandle(ref, () => inputRef.current!)

    const localizedLabel = localizeFrontendFieldLabel(label, locale)
    const localizedTopLabel = topLabel
      ? localizeFrontendFieldLabel(topLabel, locale)
      : undefined

    return (
      <div className="flex flex-col w-full">
        {localizedTopLabel && (
          <Label className="mb-2 txt-compact-medium-plus">
            {localizedTopLabel}
          </Label>
        )}
        <div className="sahil-form-input flex relative z-0 w-full txt-compact-medium">
          <input
            type={inputType}
            name={name}
            placeholder=" "
            required={required}
            className={`sahil-form-input__field pt-4 pb-1 block w-full h-11 px-4 mt-0 bg-ui-bg-field border rounded-md appearance-none focus:outline-none focus:ring-0 focus:shadow-borders-interactive-with-active border-ui-border-base hover:bg-ui-bg-field-hover ${
              type === "password" ? "sahil-form-input__field--password" : ""
            }`}
            {...props}
            ref={inputRef}
          />
          <label
            htmlFor={name}
            onClick={() => inputRef.current?.focus()}
            className="sahil-form-input__label flex items-center justify-center mx-3 px-1 transition-all absolute duration-300 top-3 -z-1 origin-0 text-ui-fg-subtle"
          >
            {localizedLabel}
            {required && <span className="text-rose-500">*</span>}
          </label>
          {type === "password" && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="sahil-form-input__password-toggle text-ui-fg-subtle focus:outline-none transition-all duration-150 outline-none focus:text-ui-fg-base"
              aria-label={
                locale === "fa"
                  ? showPassword
                    ? "پنهان‌کردن رمز عبور"
                    : "نمایش رمز عبور"
                  : showPassword
                    ? "Hide password"
                    : "Show password"
              }
            >
              {showPassword ? <Eye /> : <EyeSlash />}
            </button>
          )}
        </div>
      </div>
    )
  }
)

Input.displayName = "Input"

export default Input
